/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { VENCIMENTO_URGENCY } from '../RelatorioInspecao/constants';

export const NSIR_VENCIMENTO_CARDS = [
  {
    key: VENCIMENTO_URGENCY.OVERDUE,
    label: 'Prazo vencido',
    hint: 'NSIRs não concluídas com data de vencimento já passada',
    color: 'error',
    icon: EventBusyOutlinedIcon,
    summaryKey: 'vencidas',
  },
  {
    key: VENCIMENTO_URGENCY.TODAY,
    label: 'Vence hoje',
    hint: 'NSIRs não concluídas com vencimento na data de hoje',
    color: 'warning',
    icon: TodayOutlinedIcon,
    summaryKey: 'vencemHoje',
  },
  {
    key: VENCIMENTO_URGENCY.WEEK,
    label: 'Vence em 7 dias',
    hint: 'NSIRs não concluídas com vencimento nos próximos 7 dias',
    color: 'info',
    icon: DateRangeOutlinedIcon,
    summaryKey: 'vencemEm7Dias',
  },
  {
    key: VENCIMENTO_URGENCY.MONTH,
    label: 'Vence em 30 dias',
    hint: 'NSIRs não concluídas com vencimento nos próximos 30 dias',
    color: 'secondary',
    icon: CalendarMonthOutlinedIcon,
    summaryKey: 'vencemEm30Dias',
  },
];

export const getNsirVencimentoUrgencyLabel = (urgencyKey) => {
  const card = NSIR_VENCIMENTO_CARDS.find((item) => item.key === urgencyKey);
  return card?.label ?? null;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const MiniVencimentoCard = ({
  config,
  value,
  active,
  loading,
  onClick,
}) => {
  const Icon = config.icon;

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        flex: 1,
        minWidth: { xs: '100%', sm: 180 },
        maxWidth: { sm: 280 },
        display: 'block',
        textAlign: 'left',
        borderRadius: 1,
        border: 1,
        borderColor: (theme) =>
          active ? theme.palette[config.color].main : theme.palette.divider,
        bgcolor: (theme) =>
          active ? `${theme.palette[config.color].main}0A` : 'background.paper',
        p: 1.25,
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          bgcolor: (theme) => `${theme.palette[config.color].main}08`,
          borderColor: (theme) => theme.palette[config.color].light,
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            color: `${config.color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: (theme) => `${theme.palette[config.color].main}14`,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.25 }}>
            {loading ? (
              <Skeleton variant="text" width={32} height={28} />
            ) : (
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.1, color: `${config.color}.dark` }}
              >
                {formatNumber(value)}
              </Typography>
            )}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
              noWrap
            >
              {config.label}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.35, display: 'block' }}
          >
            {config.hint}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
};

MiniVencimentoCard.propTypes = {
  config: PropTypes.object.isRequired,
  value: PropTypes.number.isRequired,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

MiniVencimentoCard.defaultProps = {
  active: false,
  loading: false,
};

const VencimentoStrip = ({ summary, activeUrgency, onUrgencyChange, loading }) => {
  const counts = {
    vencidas: summary?.vencidas ?? 0,
    vencemHoje: summary?.vencemHoje ?? 0,
    vencemEm7Dias: summary?.vencemEm7Dias ?? 0,
    vencemEm30Dias: summary?.vencemEm30Dias ?? 0,
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Prazo de vencimento das NSIRs não concluídas no filtro atual
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        useFlexGap
        flexWrap="wrap"
      >
        {NSIR_VENCIMENTO_CARDS.map((card) => (
          <MiniVencimentoCard
            key={card.key}
            config={card}
            value={counts[card.summaryKey]}
            active={activeUrgency === card.key}
            loading={loading}
            onClick={() => {
              onUrgencyChange?.(activeUrgency === card.key ? null : card.key);
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

VencimentoStrip.propTypes = {
  summary: PropTypes.object,
  activeUrgency: PropTypes.string,
  onUrgencyChange: PropTypes.func,
  loading: PropTypes.bool,
};

VencimentoStrip.defaultProps = {
  summary: null,
  activeUrgency: null,
  onUrgencyChange: null,
  loading: false,
};

export default VencimentoStrip;
